import { useEffect, useRef, type ReactNode } from 'react';
import type { JoinRequest } from '../model';

const iconPaths = {
  home: '<path d="M3 11.5 12 4l9 7.5v8a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-8Z"/>',
  music: '<path d="M9 18V5l10-2v12"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="15" r="3"/>',
  calendar: '<rect x="4" y="5" width="16" height="15" rx="2"/><path d="M8 3v4M16 3v4M4 10h16"/>',
  folder: '<path d="M3 6.5h7l2 2h9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-12Z"/>',
  file: '<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"/><path d="M14 3v6h6"/>',
  fileText:
    '<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"/><path d="M14 3v6h6"/><path d="M8 13h8M8 17h5"/>',
  fileAudio:
    '<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"/><path d="M14 3v6h6"/><path d="M10 17v-5l5-1v5"/><circle cx="8" cy="17" r="2"/><circle cx="13" cy="16" r="2"/>',
  fileScore:
    '<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"/><path d="M14 3v6h6"/><path d="M10 17V11l5-1v5"/><circle cx="8" cy="17" r="2"/>',
  users:
    '<path d="M16 19c0-2.2-1.8-4-4-4s-4 1.8-4 4"/><circle cx="12" cy="10" r="3"/><path d="M5.5 18c.3-1.8 1.6-3.2 3.2-3.8M15.3 14.2c1.7.6 2.9 2 3.2 3.8"/><circle cx="6.5" cy="11" r="2"/><circle cx="17.5" cy="11" r="2"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  bell: '<path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/>',
  chevronLeft: '<path d="m15 5-7 7 7 7"/>',
  chevronRight: '<path d="m9 5 7 7-7 7"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  edit: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"/>',
  shield: '<path d="M12 3 5 6v5c0 4.5 2.8 8.5 7 10 4.2-1.5 7-5.5 7-10V6l-7-3Z"/>',
  trash: '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v5M14 11v5"/>'
} as const;

export type IconName = keyof typeof iconPaths;

export function Icon({ name }: { readonly name: IconName }): ReactNode {
  return <SvgIcon name={name} className="bottom-nav-icon" />;
}

export function SvgIcon({ name, className }: { readonly name: IconName; readonly className: string }): ReactNode {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: iconPaths[name] }}
    />
  );
}

export function WorkspaceModuleTopBar({
  title,
  action
}: {
  readonly title: string;
  readonly action?: {
    readonly label: string;
    readonly dataAction: string;
    readonly icon: IconName;
    readonly onClick: () => void;
  };
}): ReactNode {
  return (
    <header className="workspace-module-topbar">
      <span aria-hidden="true" />
      <h1 className="page-title" tabIndex={-1}>
        {title}
      </h1>
      {action ? (
        <button
          type="button"
          className="module-topbar-action"
          data-action={action.dataAction}
          aria-label={action.label}
          onClick={action.onClick}
        >
          <SvgIcon name={action.icon} className="module-topbar-action-icon" />
        </button>
      ) : (
        <span aria-hidden="true" />
      )}
    </header>
  );
}

export function ModuleEmptyState({ icon, body }: { readonly icon: IconName; readonly body: string }): ReactNode {
  return (
    <section className="module-empty-state">
      <span className="module-empty-icon" aria-hidden="true">
        <SvgIcon name={icon} className="module-empty-svg" />
      </span>
      <p>{body}</p>
    </section>
  );
}

export function CompactEmptyRow({ body }: { readonly body: string }): ReactNode {
  return (
    <div className="compact-empty-row">
      <p>{body}</p>
    </div>
  );
}

export function SectionLoading({ body }: { readonly body: string }): ReactNode {
  return (
    <section className="panel" aria-busy="true">
      <p>{body}</p>
    </section>
  );
}

export function RecoverableReadError({
  error,
  retryAction,
  onRetry
}: {
  readonly error: Error;
  readonly retryAction: string;
  readonly onRetry: () => void;
}): ReactNode {
  const errorRef = useRef<HTMLDivElement>(null);
  const message = formatActionError(error);
  useEffect(() => {
    errorRef.current?.focus();
  }, [error, message]);

  return (
    <div className="panel error" role="alert" tabIndex={-1} ref={errorRef}>
      <p>{message}</p>
      <button type="button" className="secondary-button" data-action={retryAction} onClick={onRetry}>
        重试
      </button>
    </div>
  );
}

export function DiscardUnsavedChangesDialog({
  body = '离开后，当前日程草稿不会保存。',
  onKeepEditing,
  onDiscard
}: {
  readonly body?: string;
  readonly onKeepEditing: () => void;
  readonly onDiscard: () => void;
}): ReactNode {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <section
      className="panel"
      data-dialog="discard-unsaved-changes"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      ref={dialogRef}
    >
      <h2>放弃未保存的更改？</h2>
      <p>{body}</p>
      <div className="footer-actions">
        <button type="button" className="secondary-button" data-action="keep-editing" onClick={onKeepEditing}>
          继续编辑
        </button>
        <button type="button" className="danger-button" data-action="discard-unsaved-changes" onClick={onDiscard}>
          放弃更改
        </button>
      </div>
    </section>
  );
}

export function BackHeader({
  title,
  action,
  label,
  pending = false,
  onBack
}: {
  readonly title: string;
  readonly action: string;
  readonly label: string;
  readonly pending?: boolean;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <header className="management-back-header">
      <button
        type="button"
        className="back-icon-button"
        data-action={action}
        disabled={pending}
        aria-busy={pending ? 'true' : undefined}
        aria-label={label}
        onClick={onBack}
      >
        <SvgIcon name="chevronLeft" className="back-icon-button-svg" />
      </button>
      <h1 className="page-title" tabIndex={-1}>
        {title}
      </h1>
    </header>
  );
}

export function TextField({
  label,
  field,
  name,
  type = 'text',
  autoComplete,
  spellCheck,
  value,
  onChange
}: {
  readonly label: string;
  readonly field: string;
  readonly name?: string;
  readonly type?: string;
  readonly autoComplete?: string;
  readonly spellCheck?: boolean;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  return (
    <label className="field" data-field={field}>
      <span>{label}</span>
      <input
        type={type}
        name={name}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </label>
  );
}

export function ConfirmedField({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className="confirmed-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function ActionCard({
  action,
  label,
  description,
  icon,
  variant = 'card',
  pending = false,
  onClick
}: {
  readonly action: string;
  readonly label: string;
  readonly description?: string;
  readonly icon?: IconName;
  readonly variant?: 'card' | 'menu';
  readonly pending?: boolean;
  readonly onClick: () => void;
}): ReactNode {
  const className = variant === 'menu' ? 'action-card management-action-card prototype-list-row' : 'action-card';

  return (
    <button
      type="button"
      className={className}
      data-action={action}
      disabled={pending}
      aria-busy={pending ? 'true' : undefined}
      onClick={() => {
        if (!pending) {
          onClick();
        }
      }}
    >
      {variant === 'menu' ? (
        <>
          {icon ? (
            <span className="action-card-icon" aria-hidden="true">
              <SvgIcon name={icon} className="action-card-icon-svg" />
            </span>
          ) : null}
          <span className="action-card-body">
            <strong>{label}</strong>
            {description ? <small>{description}</small> : null}
          </span>
        </>
      ) : (
        label
      )}
      {pending ? <LoadingAffordance /> : null}
      {variant === 'menu' ? (
        <span className="row-chevron" aria-hidden="true">
          ›
        </span>
      ) : null}
    </button>
  );
}

export function PendingButton({
  action,
  label,
  variant,
  pending,
  dataAttrs,
  onClick
}: {
  readonly action: string;
  readonly label: string;
  readonly variant: 'primary' | 'secondary';
  readonly pending: boolean;
  readonly dataAttrs?: Readonly<Record<string, string>>;
  readonly onClick: () => void;
}): ReactNode {
  const className = variant === 'primary' ? 'primary-button' : 'secondary-button';
  const dataProps = Object.fromEntries(
    Object.entries(dataAttrs ?? {}).map(([key, value]) => [`data-${toKebabCase(key)}`, value])
  );

  return (
    <button
      type="button"
      className={className}
      data-action={action}
      disabled={pending}
      aria-busy={pending ? 'true' : undefined}
      onClick={onClick}
      {...dataProps}
    >
      {label}
      {pending ? <LoadingAffordance /> : null}
    </button>
  );
}

export function LoadingAffordance(): ReactNode {
  return (
    <>
      <span className="button-loading-indicator" aria-hidden="true" />
      <span>处理中</span>
    </>
  );
}

export function ActionError({ message }: { readonly message: string | null }): ReactNode {
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (message) {
      errorRef.current?.focus();
    }
  }, [message]);

  if (!message) {
    return null;
  }

  return (
    <div className="panel error" role="alert" tabIndex={-1} ref={errorRef}>
      <p>{message}</p>
    </div>
  );
}

export function OwnJoinRequests({ requests }: { readonly requests: readonly JoinRequest[] }): ReactNode {
  if (requests.length === 0) {
    return null;
  }

  return (
    <section>
      <h2>我的加入申请</h2>
      <ul className="status-list">
        {requests.map((request) => (
          <li key={request.joinRequestId}>{`${request.nickname}：${formatJoinRequestStatus(request)}`}</li>
        ))}
      </ul>
    </section>
  );
}

export function getChoirAvatarLabel(name: string): string {
  return name.trim().slice(0, 1) || '合';
}

export function formatMemberGroupNames(memberGroupNames: readonly string[]): string {
  return memberGroupNames.length === 0 ? '未分组' : memberGroupNames.join(' · ');
}

export function formatActionError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const knownMessages: Readonly<Record<string, string>> = {
    'Email is required': '请输入邮箱。',
    'Email must be valid': '请输入有效邮箱。',
    'Email is already registered': '这个邮箱已经注册，请返回登录。',
    'Email or password is incorrect': '邮箱或密码不正确，请检查后重试。',
    'Choir name is required': '请输入合唱团名称。',
    'Invitation code is required': '请输入邀请码。',
    'Invitation code not found': '邀请码不存在，请检查后重试。',
    'Invitation code is deleted': '这个邀请码已被删除，请联系邀请人获取新的邀请码。',
    'Invitation code is expired': '这个邀请码已过期，请联系邀请人获取新的邀请码。',
    'Invitation code is not active': '这个邀请码当前不可用，请刷新后重试。',
    'Nickname is required': '请输入昵称。',
    'User is already a current season member of this choir': '你已经是该合唱团当前乐季成员。',
    'A pending join request already exists for this choir current season': '你已经提交过加入申请，请等待管理员处理。',
    'Choir is not available to current user': '当前账号不能进入这个合唱团。',
    'Current choir membership is required': '请先进入一个合唱团空间。',
    'Assignment piece name is required': '请输入曲目。',
    'Assignment due time must be valid': '请选择有效截止时间。',
    'Assignment delivery target member group is required': '请选择交付成员组。',
    'Assignment reviewer is required': '请选择评审对象。',
    'Assignment file upload failed': '文件上传失败，请重试。',
    'Assignment submission file extension is required': '请选择带有文件后缀的作业文件。',
    'Folder name is required': '请输入文件夹名称。',
    'Resource file extension is required': '请选择带有文件后缀的资料文件。',
    'Resource file upload failed': '文件上传失败，请重试。',
    'Song name is required': '请输入曲目名称。',
    'Failed to fetch': '网络连接或服务暂时不可用，请重试。',
    'Network request failed': '网络连接或服务暂时不可用，请重试。'
  };

  return knownMessages[message] ?? '操作失败，请重试。';
}

function formatJoinRequestStatus(request: JoinRequest): string {
  if (request.status === 'pending') {
    return '申请已提交，等待管理员处理。';
  }
  if (request.status === 'approved') {
    return '申请已通过。';
  }
  if (request.rejectionReason) {
    return `申请已拒绝：${request.rejectionReason}`;
  }
  return '申请已拒绝。';
}

function toKebabCase(value: string): string {
  return value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}
